/**
 * Plugins oficiais (docs/spec/web-plugins.md, "Plugins oficiais"): pacotes que os administradores da
 * configuração do servidor publicam e que qualquer mestre instala na mesa, pela aba Oficiais de
 * "Instalar plugin…". Cada um diz para que sistemas serve, e a aba põe no alto os do sistema da mesa.
 */
import { z } from 'zod';
import { IdSchema } from './domain.js';
import { systemKey } from './rooms.js';

export const OFFICIAL_PLUGIN_LIMITS = {
  /** quantos sistemas um plugin oficial marca */
  systems: 12,
  /** o tamanho de cada um (o mesmo do sistema da mesa) */
  system: 60,
} as const;

/**
 * A chave que casa o sistema da mesa com os de um plugin oficial: sem maiúsculas, acentos, espaços nem
 * pontuação ("Tormenta 20" casa com "tormenta20"; "D&D 5e" vira "dd5e").
 */
export function systemMatchKey(system: string): string {
  return systemKey(system).normalize('NFKD').replace(/[^\p{L}\p{N}]/gu, '');
}

/** Os sistemas como ficam gravados: sem espaço sobrando, sem repetidos (pela chave de casar), até o limite. */
export function cleanSystems(list: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    const system = raw.trim().replace(/\s+/g, ' ').slice(0, OFFICIAL_PLUGIN_LIMITS.system).trim();
    const key = systemMatchKey(system);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(system);
    if (out.length === OFFICIAL_PLUGIN_LIMITS.systems) break;
  }
  return out;
}

/** O plugin serve para o sistema desta mesa (texto livre, comparado pela `systemMatchKey`). */
export function servesSystem(systems: readonly string[], roomSystem: string): boolean {
  const key = systemMatchKey(roomSystem);
  return key !== '' && systems.some((s) => systemMatchKey(s) === key);
}

/** Texto, ou um texto por língua, como no manifesto. */
const LocalizedSchema = z.union([z.string().max(120), z.record(z.string(), z.string().max(120))]);
const OfficialDataTypeSchema = z.object({ type: z.string().max(120), title: LocalizedSchema });

/** Um plugin oficial como o mestre o vê na aba Oficiais: a versão publicada agora. */
export const OfficialPluginSchema = z.object({
  id: IdSchema,
  /** o `id` do manifesto: a mesa com um plugin desse id tem este plugin (instalado ou para atualizar) */
  moduleId: z.string().min(1).max(120),
  name: LocalizedSchema,
  version: z.string().max(40),
  author: z.string().max(120).optional(),
  description: z.union([z.string().max(1000), z.record(z.string(), z.string().max(1000))]).optional(),
  sheets: z.array(OfficialDataTypeSchema).max(50),
  tables: z.array(OfficialDataTypeSchema).max(50),
  systems: z.array(z.string().max(OFFICIAL_PLUGIN_LIMITS.system)).max(OFFICIAL_PLUGIN_LIMITS.systems),
  /** o ícone do pacote (PNG ou SVG), servido com os arquivos dele; null sem ícone */
  iconUrl: z.string().max(800).nullable(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  updatedAt: z.number().int().nonnegative(),
});
export type OfficialPlugin = z.infer<typeof OfficialPluginSchema>;

/** Um plugin oficial na seção Plugins oficiais da administração. */
export const AdminOfficialPluginSchema = OfficialPluginSchema.extend({
  /** fora do catálogo: as mesas que já têm continuam com ele */
  hidden: z.boolean(),
  /** as mesas com o plugin instalado do catálogo */
  rooms: z.number().int().nonnegative(),
  /** dessas, as que estão numa versão anterior à publicada */
  outdated: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
  createdByName: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type AdminOfficialPlugin = z.infer<typeof AdminOfficialPluginSchema>;

/** Um sistema que as mesas usam, para sugerir no cadastro (o nome mais comum e quantas mesas). */
export const KnownSystemSchema = z.object({ name: z.string(), rooms: z.number().int().nonnegative() });
export type KnownSystem = z.infer<typeof KnownSystemSchema>;

/** O `detail` de uma ação sobre um plugin oficial no registro, em JSON (a linha não tem conta como alvo). */
export const AdminPluginLogDetailSchema = z.object({
  officialId: z.string(),
  moduleId: z.string(),
  /** a versão publicada depois da ação */
  version: z.string(),
  /** a versão de antes (versão nova) */
  from: z.string().optional(),
  /** os sistemas depois da ação (publicar e mudar os sistemas) */
  systems: z.array(z.string()).optional(),
});
export type AdminPluginLogDetail = z.infer<typeof AdminPluginLogDetailSchema>;

export function parseAdminPluginLogDetail(detail: string): AdminPluginLogDetail | null {
  try {
    const parsed = AdminPluginLogDetailSchema.safeParse(JSON.parse(detail));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
